import React from 'react';
import { isNil, range, toNumber, unset } from 'lodash';
import { TradeIn } from '@upstart/harmony/lib/legos/dealTypes';
import { parseCurrencyTruncated } from '@upstart/harmony/lib/form-utils';
import { FinanceResponseData } from '@upstart/harmony/lib/legos/financingTypes';
import { formatCurrency } from 'util/format';
import { FormTable } from './FormItems';
import Input, { PriceInput } from '../../../../components/PencilEditor/Input';

const VIN_LENGTH = 17;

const getTradeColumns = (maxTradeIns: number) =>
  range(maxTradeIns).map(i => ({
    title: `Vehicle ${i + 1}`,
    width: '40%',
  }));

function getNetTrade(tradeIn?: TradeIn) {
  const allowance = toNumber(tradeIn?.tradeInAllowance ?? 0);
  const payOff = toNumber(tradeIn?.payOff ?? 0);
  return allowance - payOff;
}

function getNetTradeLabel(tradeIn?: TradeIn) {
  if (isNil(tradeIn)) {
    return <b>N/A</b>;
  }
  return <b>{formatCurrency(getNetTrade(tradeIn))}</b>;
}

export function TradeInItem(props: {
  type?: keyof TradeIn | 'netTrade';
  value?: React.ReactElement | string;
  responseData: FinanceResponseData | undefined;
  fetchResponseWithUpdatedData: (data: Partial<FinanceResponseData>) => void;
  maxTradeIns: number;
  required?: boolean;
}) {
  const { type, responseData, fetchResponseWithUpdatedData, required } = props;

  const tradeIns = responseData?.tradeIns ?? [];

  const updateTradeInList = (index: number, newData: Partial<TradeIn>) => {
    if (tradeIns[index]) {
      tradeIns[index] = { ...tradeIns[index], ...newData };
    }
    return tradeIns;
  };

  return (
    <FormTable
      columns={getTradeColumns(props.maxTradeIns)}
      rows={[
        {
          descriptions: range(props.maxTradeIns).map(i => {
            const tradeIn = tradeIns[i];
            const isTradeInPresent = !isNil(tradeIn);

            const onChange = (data: Partial<TradeIn>) =>
              fetchResponseWithUpdatedData({
                tradeIns: updateTradeInList(i, data),
              });

            if (type === 'netTrade') {
              return getNetTradeLabel(tradeIn);
            }
            if (type === 'tradeInAllowance') {
              return (
                <PriceInput
                  value={toNumber(tradeIn?.tradeInAllowance) || 0}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={v => {
                    onChange({ tradeInAllowance: parseCurrencyTruncated(v) });
                  }}
                />
              );
            }
            if (type === 'payOff') {
              return (
                <PriceInput
                  value={toNumber(tradeIn?.payOff) || 0}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={v => {
                    onChange({ payOff: parseCurrencyTruncated(v) });
                  }}
                />
              );
            }
            if (type === 'currentLienHolder') {
              // Lien holder field is only enabled and required if total down payment balance is negative and the net trade for this specific trade in is negative.
              const isLienHolderRequired =
                isTradeInPresent &&
                tradeIn.payOff &&
                Number(tradeIn.payOff) > 0;
              if (!isLienHolderRequired) {
                unset(tradeIn, 'currentLienHolder');
              }
              return (
                <Input
                  value={tradeIn?.currentLienHolder}
                  placeholder="N/A"
                  disabled={!isLienHolderRequired}
                  required={isLienHolderRequired && required}
                  onChange={(v: string) => onChange({ currentLienHolder: v })}
                />
              );
            }
            if (type === 'id') {
              return (
                <Input
                  value={tradeIn?.id}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(v: string) => onChange({ id: v })}
                  required={isTradeInPresent && required}
                  minLength={VIN_LENGTH}
                  maxLength={VIN_LENGTH}
                />
              );
            }
            if (type === 'year') {
              return (
                <Input
                  value={tradeIn?.year?.name}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(y: string) => onChange({ year: { name: y } })}
                  required={isTradeInPresent && required}
                />
              );
            }
            if (type === 'make') {
              return (
                <Input
                  value={tradeIn?.make?.name}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(m: string) => onChange({ make: { name: m } })}
                  required={isTradeInPresent && required}
                />
              );
            }
            if (type === 'model') {
              return (
                <Input
                  value={tradeIn?.model?.name}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(m: string) => onChange({ model: { name: m } })}
                  required={isTradeInPresent && required}
                />
              );
            }
            if (type === 'trim') {
              return (
                <Input
                  value={tradeIn?.trim?.name}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(t: string) => onChange({ trim: { name: t } })}
                  required={isTradeInPresent && required}
                />
              );
            }
            if (type === 'mileage') {
              return (
                <Input
                  value={tradeIn?.mileage}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(m: string) => onChange({ mileage: m })}
                  required={isTradeInPresent && required}
                />
              );
            }
            if (type === 'license') {
              return (
                <Input
                  value={tradeIn?.license}
                  placeholder="N/A"
                  disabled={!isTradeInPresent}
                  onChange={(m: string) => onChange({ license: m })}
                  required={isTradeInPresent && required}
                />
              );
            }
            return null;
          }),
          value: props.value,
        },
      ]}
    />
  );
}

export default TradeInItem;
